"use client";

import React from "react";
import { Check } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const packages = [{
  id: 1,
  title: "Intimate Private Dining",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1505473/img/private_dining_ubud.png",
  price: "From $45 / pax",
  features: ["Curated Set Menu Options", "Serene Rice Field Views", "Private Butler Service", "Elegant Floral Decor"],
  link: "/ConnectPage"
}, {
  id: 2,
  title: "Bamboo Hall Weddings",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1505473/img/bamboo_wedding_ubud.png",
  price: "From $2,500",
  features: ["Buffet or Plated Service", "Natural Bamboo Architecture", "Full Sound & Light System", "Dedicated Event Manager"],
  link: "/ConnectPage"
}, {
  id: 3,
  title: "Creative Workshop Hub",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1505473/img/workshop_ubud.png",
  price: "From $25 / pax",
  features: ["Healthy Catering Platters", "Coffee & Tea Station", "Flexible Modular Seating", "High-Speed Fiber Internet"],
  link: "/ConnectPage"
}, {
  id: 4,
  title: "Corporate Retreats",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1505473/img/corporate_retreat_ubud.png",
  price: "Custom Quote",
  features: ["Full Day Venue Access", "Team Building Setup", "Gourmet Lunch Buffet", "Ample On-site Parking"],
  link: "/ConnectPage"
}, {
  id: 5,
  title: "Signature Celebrations",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1505473/img/birthday_party_ubud.png",
  price: "From $35 / pax",
  features: ["Customizable Event Themes", "Artisan Cake & Dessert Table", "Welcome Mocktail Service", "Professional Photographer"],
  link: "/ConnectPage"
}];
export default function SignatureExperiences() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="77593" id="curated-event-packages" className="dark bg-background text-foreground py-24 md:py-32 overflow-hidden relative">
      {}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,var(--color-primary)_0%,transparent_20%)] opacity-5 pointer-events-none" />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div ref={headerRef} className={`max-w-3xl mx-auto text-center mb-16 md:mb-24 transition-all duration-1000 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight mb-6">
            Curated Event Packages
          </h2>
          <div className="w-24 h-[1px] bg-primary mx-auto mb-6" />
          <p className="text-muted-foreground text-lg md:text-xl font-light">
            Thoughtfully designed experiences in the heart of Peliatan, blending 
            natural bamboo aesthetics with premium hospitality for your special moments.
          </p>
        </div>

        <div className="relative px-0 sm:px-12">
          <Carousel opts={{
          align: "start",
          loop: true
        }} className="w-full">
            <CarouselContent className="-ml-4 md:-ml-6">
              {packages.map((pkg, i) => <CarouselItem key={pkg.id} data-index={i} className="pl-4 md:pl-6 md:basis-1/2 lg:basis-1/3 xl:basis-1/4">
                  <Card className="bg-card border-border/40 h-full flex flex-col rounded-xl group hover:border-secondary/50 transition-colors duration-500 shadow-lg">
                    <div className="aspect-[4/5] relative overflow-hidden rounded-t-xl">
                      <div className="absolute inset-0 bg-black/10 group-hover:bg-transparent transition-colors duration-500 z-10" />
                      <img src={pkg.image} alt={pkg.title} className="object-cover w-full h-full transition-transform duration-1000 group-hover:scale-105" loading="lazy" />
                    </div>
                    
                    <CardHeader className="pt-8 pb-4 px-6 md:px-8 text-center border-b border-border/20">
                      <CardTitle className="font-serif text-2xl md:text-3xl font-medium tracking-wide mb-2 text-foreground">
                        {pkg.title}
                      </CardTitle>
                      <div className="font-sans text-xl text-primary font-semibold mt-4">
                        {pkg.price}
                      </div>
                    </CardHeader>

                    <CardContent className="pt-6 pb-8 px-6 md:px-8 flex-grow">
                      <ul className="space-y-4">
                        {pkg.features.map((feature, index) => <li key={index} className="flex items-start gap-3">
                            <Check className="w-5 h-5 text-secondary shrink-0 mt-0.5" />
                            <span className="text-muted-foreground text-sm md:text-base font-light leading-relaxed">
                              {feature}
                            </span>
                          </li>)}
                      </ul>
                    </CardContent>

                    <CardFooter className="px-6 md:px-8 pb-8 pt-0 mt-auto">
                      <Button asChild variant="outline" className="w-full rounded-lg border-primary/50 text-primary hover:bg-primary hover:text-primary-foreground transition-all duration-300 h-12 text-sm tracking-widest uppercase font-sans">
                        <Link to={pkg.link}>Enquire Now</Link>
                      </Button>
                    </CardFooter>
                  </Card>
                </CarouselItem>)}
            </CarouselContent>
            <div className="hidden sm:block">
              <CarouselPrevious className="border-primary/30 text-primary hover:bg-primary hover:text-primary-foreground bg-background/80 backdrop-blur-sm" />
              <CarouselNext className="border-primary/30 text-primary hover:bg-primary hover:text-primary-foreground bg-background/80 backdrop-blur-sm" />
            </div>
          </Carousel>
        </div>
      </div>
    </section>;
}